export const resolveAutomationTestId = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) {
      continue;
    }

    const normalizedCandidate = String(candidate)
      .trim()
      .replace(/\s+/g, "-");

    if (normalizedCandidate) {
      return normalizedCandidate;
    }
  }

  return undefined;
};

export const normalizeAutomationTestIdSegment = (value, fallback = "unknown") => {
  const normalized = String(value ?? "")
    .trim()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return normalized || fallback;
};

export const normalizeTestIdSegment = (value, fallback = "item") => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
};

export const normalizeReadableTestIdSegment = (value, fallback = "item") => normalizeTestIdSegment(
  String(value ?? "")
    .replace(/_/g, "-")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2"),
  fallback
);

export const isOpaqueTestIdValue = (value) => {
  const normalized = String(value ?? "").trim();

  return (
    /^[a-f0-9]{24}$/i.test(normalized) ||
    /^[0-9]{4,}$/.test(normalized)
  );
};

const DEFAULT_RECORD_KEYS = [
  "applicationNo",
  "applicationNumber",
  "application_no",
  "application_number",
  "brokerReferenceId",
  "transactionNumber",
  "transactionNo",
  "transaction_number",
  "transaction_no",
  "reportNumber",
  "reportNo",
  "report_number",
  "report_no",
  "ruleNumber",
  "ruleNo",
  "rule_number",
  "rule_no",
  "code",
  "key",
  "internalName",
  "slug",
];

const FALLBACK_RECORD_KEYS = [
  "_id",
  "id",
  "applicationId",
  "application_id",
  "accountId",
  "account_id",
  "documentId",
  "document_id",
  "userId",
  "user_id",
  "roleId",
  "role_id",
  "uuid",
];

const getRecordValue = (record, key) => {
  if (!record || !key) return undefined;
  return key.split(".").reduce((value, part) => value?.[part], record);
};

const normalizeRecordKey = (key, fallbackPrefix) => normalizeTestIdSegment(
  String(key ?? "")
    .replace(/\./g, "-")
    .replace(/_/g, "-")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2"),
  fallbackPrefix
);

export const buildRecordTestIdSegment = (
  record,
  {
    preferredKeys = [],
    fallbackPrefix = "record",
    fallback = "row",
    includeKeyName = true,
    allowOpaqueValues = false,
    allowOpaqueFallback = false,
  } = {}
) => {
  const meaningfulKeys = [...preferredKeys, ...DEFAULT_RECORD_KEYS];

  for (const key of meaningfulKeys) {
    const value = getRecordValue(record, key);
    if (value !== null && value !== undefined && String(value).trim()) {
      if (!allowOpaqueValues && isOpaqueTestIdValue(value)) {
        continue;
      }

      const normalizedValue = normalizeReadableTestIdSegment(value, fallback);
      const normalizedKey = normalizeRecordKey(key, fallbackPrefix);
      return includeKeyName ? `${normalizedKey}-${normalizedValue}` : normalizedValue;
    }
  }

  if (allowOpaqueFallback) {
    for (const key of FALLBACK_RECORD_KEYS) {
      const value = getRecordValue(record, key);
      if (value !== null && value !== undefined && String(value).trim()) {
        return `${fallbackPrefix}-${normalizeReadableTestIdSegment(value, fallback)}`;
      }
    }
  }

  return fallback;
};

export const normalizeAutomationTestId = (value, fallback = "") => {
  const rawValue = value === null || value === undefined || value === "" ? fallback : value;
  return normalizeAutomationTestIdSegment(rawValue, fallback);
};

export const buildAutomationChildTestId = (baseTestId, suffix) => {
  const resolvedBaseTestId = resolveAutomationTestId(baseTestId);
  const resolvedSuffix = normalizeAutomationTestIdSegment(suffix, "");

  if (!resolvedBaseTestId) {
    return undefined;
  }

  if (!resolvedSuffix) {
    return resolvedBaseTestId;
  }

  return `${resolvedBaseTestId}--${resolvedSuffix}`;
};

export const buildAutomationValueTestId = (baseTestId, value) => {
  const resolvedBaseTestId = resolveAutomationTestId(baseTestId);
  const resolvedValue = normalizeAutomationTestIdSegment(value, "");

  if (!resolvedBaseTestId) {
    return undefined;
  }

  if (!resolvedValue) {
    return resolvedBaseTestId;
  }

  return `${resolvedBaseTestId}-${resolvedValue}`;
};

export const buildAutomationOptionTestId = (baseTestId, value) => {
  const resolvedBaseTestId = resolveAutomationTestId(baseTestId);
  const resolvedValue = normalizeAutomationTestIdSegment(value, "");

  if (!resolvedBaseTestId) {
    return undefined;
  }

  if (!resolvedValue) {
    return resolvedBaseTestId;
  }

  return `${resolvedBaseTestId}--option-${resolvedValue}`;
};
